import graphQLAdapter from "@/adapters/graphQL.adapter.js";
import User from "@/entities/user.entity.js";

/**
 * User Service Class
 *
 * @export
 * @class
 */
export default class {
  constructor () {
    // eslint-disable-next-line
    this.adapter = new graphQLAdapter();

    // User
    this.getMe = getMe.bind(this);
    this.getUser = getUser.bind(this);
    this.getUsers = getUsers.bind(this);
    this.userCreate = userCreate.bind(this);
    this.userUpdate = userUpdate.bind(this);
    this.userDelete = userDelete.bind(this);
    // Client Groups
    this.getClientGroups = getClientGroups.bind(this);
  }
  /**
   * Get the login user
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the list of users
   */
  async me () {
    await this.adapter.init();
    try {
      if (!this.adapter.isAuthenticated) {
        return null;
      }
      const res = await this.getMe();
      return res.data.me;
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  /**
   * Get the users
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the list of users
   */
  async list (option) {
    await this.adapter.init();
    try {
      if (!this.adapter.isAuthenticated) {
        return [];
      }
      const res = await this.getUser(option);
      return res.data.user;
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  /**
   * Get the list of users
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the list of users
   */
  async get (option) {
    await this.adapter.init();
    try {
      if (!this.adapter.isAuthenticated) {
        return [];
      }
      const res = await this.getUsers(option);
      return res.data.users.map(el => User.fromJson(el));
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  /**
   * Get the list of client groups
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the list of client groups
   */
  async getClientList (option) {
    await this.adapter.init();
    try {
      if (!this.adapter.isAuthenticated) {
        return [];
      }
      const res = await this.getClientGroups(option);
      return res.data.clientGroups.map(el => User.fromJson(el));
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  /**
   * Create User
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the user
   */
  async insert (option) {
    const res = await this.userCreate(option);
    if (res.data.userCreate) {
      return User.fromJson(res.data.userCreate);
    }
    return res;
  }
  /**
   * Update User
   *
   * @static
   * @returns {Promise<Object|null>} Promise object represents the user
   */
  async update (option) {
    const res = await this.userUpdate(option);
    if (res.data.userUpdate) {
      return User.fromJson(res.data.userUpdate);
    }
    return res;
  }
  /**
   * Delete User
   */
  async delete (option) {
    const res = await this.userDelete(option);
    if (res.data.userDelete) {
      return User.fromJson(res.data.userDelete);
    }
    return res;
  }
}
/**
 * Get my user info
 */
function getMe () {
  const query = `query {
    me {
      id
      name
      role
      email
      password
      clientGroups {
        id
        name
        deliveryDestinations {
          id
          name
        }
        showSKWriteFont
        showGoldFilmColor
        showHandCarveWriteFont
      }
    }
  }
  `;
  return this.adapter.query(query, {});
}
/**
 * Get the user by user id param
 */
function getUser (option) {
  const query = `query ($userId: ID!){
    user(userId: $userId) {
      id
      name
      role
      email
      password
      clientGroups {
        id
        name
        deliveryDestinations {
          id
          name
        }
      }
    }
  }
  `;
  return this.adapter.query(query, option);
}
/**
 * Get the list of users by clientGroupId param
 */
function getUsers (
  options = {
    q: {
      clientGroupId: null,
      role: null,
    },
  }
) {
  const query = `query ($q: UserFilterConditions) {
    users(q: $q) {
      id
      name
      role
    }
  }`;
  return this.adapter.query(query, options);
}
/**
 * Get the list of client groups
 */
function getClientGroups (option) {
  const query = `query($q:ClientGroupFilterConditions,$first:Int,$offset:Int){
    clientGroups(q: $q, first: $first, offset:$offset) {
      id
      name
      zipcode
      address
      tel
      users {
        id
      }
      deliveryDestinations {
        id
        name
      }
    }
  }`;
  return this.adapter.query(query, option);
}
/**
 * Create User
 */
function userCreate (options) {
  const mutation = `mutation ($UserInput: UserInput!) {
    userCreate(user: $UserInput) {
      id
      name
      clientGroups {
        id
        name
      }
      role
      avatarImageUrl
      email
      password
      createdAt
    }
  }`;
  return this.adapter.mutation(mutation, options);
}
/**
 * Update User
 */
function userUpdate (options = {}) {
  const mutation = `mutation ($userId: ID!, $user: UserInput!) {
    userUpdate (userId: $userId, user: $user) {
      id
      name
      clientGroups {
        id
        name
      }
      role
      avatarImageUrl
      email
      password
      createdAt
    }
  }`;
  return this.adapter.mutation(mutation, options);
}
/**
 * Delete User
 */
function userDelete (options = {}) {
  const mutation = `mutation ($userId: ID!) {
    userDelete (userId: $userId) {
      id
      name
      clientGroups {
        id
        name
      }
      role
      avatarImageUrl
      email
      password
      createdAt
    }
  }`;
  return this.adapter.mutation(mutation, options);
}
